import { z } from "zod/v4";
import {
  CameraSubject,
  ClientApiFeaturePathParams,
  defineEndpoint,
  defineResource,
  SingleResponse,
} from "@visioni/a3s-client-api";
import { isoDatetimeToDateString } from "@visioni/a3s-codecs";
import { FEATURE_NAME } from "../constants";

/**
 * Legacy day-page size used by timelapse frame selection logic in media
 * services. The app no longer renders frame pages, but render-source queries
 * still rely on the same hourly/day assumption.
 */
export const TIMELAPSE_FRAMES_PER_PAGE = 24;

/**
 * Maximum span allowed between `startTime` and `endTime` for any timelapse
 * video request. Hard cap protects the export pipeline from unbounded ranges;
 * the default UX still uses 7 days.
 */
export const TIMELAPSE_MAX_RANGE_DAYS = 14;

/**
 * Maximum range expressed in milliseconds. Exported so wire-schema validation
 * (this file) and service-level validation (`media-service.ts`) reference a
 * single source of truth.
 */
export const TIMELAPSE_MAX_RANGE_MS = TIMELAPSE_MAX_RANGE_DAYS * 24 * 60 * 60 * 1000;

const TIMELAPSE_MAX_RANGE_MESSAGE = `endTime - startTime must be <= ${String(TIMELAPSE_MAX_RANGE_DAYS)} days`;

/**
 * Camera timezone is the source of truth for timelapse export boundaries.
 *
 * IANA timezone identifiers (e.g. `Australia/Sydney`); validation is
 * performed in handlers via the standard `Intl.DateTimeFormat` constructor
 * to avoid duplicating tz-database lists in the wire schema.
 */
const TimezoneId = z.string().min(1);

// ==================== Path Params ====================

export type RequestTimelapseVideoPathParams = ClientApiFeaturePathParams;
export type GetTimelapseVideoPathParams = ClientApiFeaturePathParams;

// ==================== Video Export ====================

/**
 * Status states for the asynchronous timelapse export job.
 *
 * - `queued`: job accepted, waiting for the renderer worker
 * - `processing`: renderer is downloading frames / running ffmpeg
 * - `ready`: MP4 is uploaded; status response includes a signed URL
 * - `failed`: rendering failed; status response includes a failure reason
 */
export const TimelapseVideoStatus = z.enum(["queued", "processing", "ready", "failed"]);

export type TimelapseVideoStatus = z.output<typeof TimelapseVideoStatus>;

/**
 * Body for `POST /timelapse/video`.
 */
export const RequestTimelapseVideoBody = z
  .object({
    startTime: isoDatetimeToDateString,
    endTime: isoDatetimeToDateString,
    cameraTimezone: TimezoneId,
  })
  .refine((b) => new Date(b.startTime).getTime() <= new Date(b.endTime).getTime(), {
    message: "startTime must be <= endTime",
    path: ["endTime"],
  })
  .refine((b) => new Date(b.endTime).getTime() - new Date(b.startTime).getTime() <= TIMELAPSE_MAX_RANGE_MS, {
    message: TIMELAPSE_MAX_RANGE_MESSAGE,
    path: ["endTime"],
  });

export type RequestTimelapseVideoBody = z.output<typeof RequestTimelapseVideoBody>;

export const TimelapseVideoSignedUrl = z.object({
  url: z.string(),
  expiresAt: z.string(),
});

export type TimelapseVideoSignedUrl = z.output<typeof TimelapseVideoSignedUrl>;

export const TimelapseVideoItem = z.object({
  subject: CameraSubject,
  status: TimelapseVideoStatus,
  startTime: z.string(),
  endTime: z.string(),
  cameraTimezone: TimezoneId,
  /** Signed download URL — only set when `status === "ready"`. */
  video: TimelapseVideoSignedUrl.optional(),
  /** Human-readable failure reason — only set when `status === "failed"`. */
  failureReason: z.string().optional(),
  requestedAt: z.string(),
  updatedAt: z.string(),
});

export type TimelapseVideoItem = z.output<typeof TimelapseVideoItem>;

export const RequestTimelapseVideoResponse = SingleResponse(TimelapseVideoItem);
export type RequestTimelapseVideoResponse = z.output<typeof RequestTimelapseVideoResponse>;

/**
 * Query params for `GET /timelapse/video`. The same window identifies the
 * job, so the GET request must echo the original `startTime`/`endTime`/
 * `cameraTimezone` triple used at request time.
 */
export const GetTimelapseVideoQueryParams = z
  .object({
    startTime: isoDatetimeToDateString,
    endTime: isoDatetimeToDateString,
    cameraTimezone: TimezoneId,
  })
  .refine((q) => new Date(q.startTime).getTime() <= new Date(q.endTime).getTime(), {
    message: "startTime must be <= endTime",
    path: ["endTime"],
  })
  .refine((q) => new Date(q.endTime).getTime() - new Date(q.startTime).getTime() <= TIMELAPSE_MAX_RANGE_MS, {
    message: TIMELAPSE_MAX_RANGE_MESSAGE,
    path: ["endTime"],
  });

export type GetTimelapseVideoQueryParams = z.output<typeof GetTimelapseVideoQueryParams>;

export const GetTimelapseVideoResponse = SingleResponse(TimelapseVideoItem);
export type GetTimelapseVideoResponse = z.output<typeof GetTimelapseVideoResponse>;

// ==================== Resources & Endpoints ====================

export const timelapseVideoResource = defineResource({
  feature: FEATURE_NAME,
  resource: "timelapse/video",
});

export const requestTimelapseVideoEndpoint = defineEndpoint(timelapseVideoResource, {
  method: "POST",
  bodySchema: RequestTimelapseVideoBody,
  responseSchema: RequestTimelapseVideoResponse,
});

export const getTimelapseVideoEndpoint = defineEndpoint(timelapseVideoResource, {
  responseSchema: GetTimelapseVideoResponse,
  querySchema: GetTimelapseVideoQueryParams,
});
